import React, { useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as PendingIcon,
  AccountBalance as BankIcon,
  Shield as ShieldIcon,
  Download as DownloadIcon,
  ReceiptLong as ReceiptIcon
} from '@mui/icons-material';

export default function ClientInvoicePrintModal({
  open,
  onClose,
  invoice,
  onOpenPayment
}) {
  if (!invoice) return null;

  const isPaid = invoice.status === 'paid' || (Number(invoice.balanceDue) || 0) <= 0;
  const isPartial = invoice.status === 'partially_paid';

  const handlePrint = () => {
    window.print();
  };

  const currency = invoice.currency || 'INR';
  const currencySymbol = currency === 'USD' ? '$' : '₹';

  const bankName = invoice.companyBillingProfile?.bankName || 'IndusInd Bank';
  const accName = invoice.companyBillingProfile?.accountName || 'VERVEAILABS PRIVATE LIMITED';
  const accNum = invoice.companyBillingProfile?.accountNumber || '251401230904';
  const ifsc = invoice.companyBillingProfile?.ifscCode || 'INDB0000866';
  const branch = invoice.companyBillingProfile?.branch || 'Bengaluru';
  const upiId = invoice.companyBillingProfile?.upiId || '251401230904@indus';

  const formatMoney = (amount) => {
    return `${currencySymbol}${Number(amount || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0B0F19',
          color: '#F8FAFC',
          borderRadius: 3,
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
          overflow: 'hidden',
          '@media print': {
            boxShadow: 'none',
            border: 'none',
            bgcolor: '#FFFFFF',
            color: '#000000',
            maxWidth: '100% !important',
            m: 0,
            p: 0,
          }
        }
      }}
    >
      {/* 1. Header Toolbar (Hidden in print) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          bgcolor: '#111827',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          '@media print': { display: 'none !important' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReceiptIcon sx={{ color: '#06b6d4', fontSize: 24 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.01em' }}>
            Tax Invoice {invoice.invoiceNumber}
          </Typography>
          <Chip
            label={invoice.status?.toUpperCase()?.replace('_', ' ') || 'ISSUED'}
            size="small"
            sx={{
              bgcolor: isPaid ? 'rgba(16, 185, 129, 0.15)' : isPartial ? 'rgba(245, 158, 11, 0.15)' : 'rgba(6, 182, 212, 0.15)',
              color: isPaid ? '#34d399' : isPartial ? '#fbbf24' : '#22d3ee',
              fontWeight: 800,
              fontSize: '10px',
              border: `1px solid ${isPaid ? 'rgba(16, 185, 129, 0.4)' : isPartial ? 'rgba(245, 158, 11, 0.4)' : 'rgba(6, 182, 212, 0.4)'}`
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {!isPaid && onOpenPayment && (
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                onClose();
                onOpenPayment(invoice);
              }}
              sx={{
                bgcolor: '#10b981',
                color: '#07090E',
                fontWeight: 700,
                fontSize: '12px',
                px: 2,
                '&:hover': { bgcolor: '#34d399' }
              }}
            >
              Pay / Submit UTR
            </Button>
          )}

          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon sx={{ fontSize: '16px !important' }} />}
            onClick={handlePrint}
            sx={{
              borderColor: 'rgba(6, 182, 212, 0.4)',
              color: '#22d3ee',
              bgcolor: 'rgba(6, 182, 212, 0.08)',
              fontWeight: 700,
              fontSize: '12px',
              px: 2,
              '&:hover': { bgcolor: 'rgba(6, 182, 212, 0.18)', borderColor: '#06b6d4' }
            }}
          >
            Print / Save PDF
          </Button>

          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: '#94A3B8', '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.08)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* 2. Vector Printable Invoice Body */}
      <DialogContent
        sx={{
          p: { xs: 3, md: 5 },
          bgcolor: '#0B0F19',
          color: '#F8FAFC',
          '@media print': {
            p: 3,
            bgcolor: '#FFFFFF !important',
            color: '#000000 !important',
          }
        }}
      >
        {/* Printable Container */}
        <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
          {/* Top Legal Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 4 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: '#22d3ee', letterSpacing: '-0.02em', '@media print': { color: '#0891b2 !important' } }}>
                VERVEAILABS PRIVATE LIMITED
              </Typography>
              <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 0.5, '@media print': { color: '#475569 !important' } }}>
                Autonomous AI Systems • Enterprise Cloud Infrastructure • B2B Solutions
              </Typography>
              <Box sx={{ mt: 1, fontSize: '11px', color: '#64748B', lineHeight: 1.5, '@media print': { color: '#334155 !important' } }}>
                <div>CIN: U72900KA2024PTC123456</div>
                <div>GSTIN: 29AAGCV1234A1Z5 • PAN: AAGCV1234A</div>
                <div>Registered Address: HSR Layout, Sector 3, Bengaluru, Karnataka 560102, India</div>
                <div>Accounts Desk: accounts@verveailabs.com • billing@verveai.co</div>
              </Box>
            </Box>

            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#FFFFFF', letterSpacing: '0.05em', '@media print': { color: '#000000 !important' } }}>
                TAX INVOICE
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#06b6d4', mt: 0.5, '@media print': { color: '#0891b2 !important' } }}>
                {invoice.invoiceNumber}
              </Typography>
              <Box sx={{ mt: 1, fontSize: '11px', color: '#94A3B8', '@media print': { color: '#475569 !important' } }}>
                <div><strong>Date of Issue:</strong> {invoice.issueDate}</div>
                <div><strong>Due Date:</strong> {invoice.dueDate}</div>
                <div><strong>Place of Supply:</strong> Karnataka (29)</div>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)', '@media print': { borderColor: '#E2E8F0 !important' } }} />

          {/* Billed To / Client Coordinates */}
          <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.08)', mb: 3.5, '@media print': { bgcolor: '#F8FAFC !important', border: '1px solid #CBD5E1 !important' } }}>
            <Typography variant="caption" sx={{ color: '#06b6d4', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1, '@media print': { color: '#0891b2 !important' } }}>
              Billed To (Client / Entity Details)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={7}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#FFFFFF', '@media print': { color: '#000000 !important' } }}>
                  {invoice.clientCompanyName || invoice.clientName || 'Valued Client'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#94A3B8', mt: 0.3, '@media print': { color: '#334155 !important' } }}>
                  Attn: {invoice.clientName || 'Principal Contact'}
                </Typography>
                {invoice.clientEmail && (
                  <Typography variant="caption" sx={{ color: '#64748B', display: 'block', '@media print': { color: '#475569 !important' } }}>
                    Email: {invoice.clientEmail}
                  </Typography>
                )}
                {invoice.clientAddress && (
                  <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.5, '@media print': { color: '#475569 !important' } }}>
                    {invoice.clientAddress}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={5} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography variant="body2" sx={{ color: '#94A3B8', '@media print': { color: '#334155 !important' } }}>
                  <strong>Client GSTIN:</strong> {invoice.clientGstin || 'Unregistered / Consumer'}
                </Typography>
                {invoice.milestoneTitle && (
                  <Typography variant="body2" sx={{ color: '#94A3B8', mt: 0.5, '@media print': { color: '#334155 !important' } }}>
                    <strong>Scope / Milestone:</strong> {invoice.milestoneTitle}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Line Items Table */}
          <Table sx={{ mb: 3, border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2, '@media print': { border: '1px solid #CBD5E1 !important' } }}>
            <TableHead sx={{ bgcolor: 'rgba(255, 255, 255, 0.04)', '@media print': { bgcolor: '#F1F5F9 !important' } }}>
              <TableRow>
                <TableCell sx={{ color: '#94A3B8', fontWeight: 700, fontSize: '11px', '@media print': { color: '#334155 !important' } }}>#</TableCell>
                <TableCell sx={{ color: '#94A3B8', fontWeight: 700, fontSize: '11px', '@media print': { color: '#334155 !important' } }}>DESCRIPTION OF SERVICES</TableCell>
                <TableCell sx={{ color: '#94A3B8', fontWeight: 700, fontSize: '11px', '@media print': { color: '#334155 !important' } }}>SAC CODE</TableCell>
                <TableCell align="right" sx={{ color: '#94A3B8', fontWeight: 700, fontSize: '11px', '@media print': { color: '#334155 !important' } }}>QTY</TableCell>
                <TableCell align="right" sx={{ color: '#94A3B8', fontWeight: 700, fontSize: '11px', '@media print': { color: '#334155 !important' } }}>RATE</TableCell>
                <TableCell align="right" sx={{ color: '#94A3B8', fontWeight: 700, fontSize: '11px', '@media print': { color: '#334155 !important' } }}>AMOUNT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(invoice.items || []).map((item, idx) => (
                <TableRow key={idx} sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)', '@media print': { borderBottom: '1px solid #E2E8F0 !important' } }}>
                  <TableCell sx={{ color: '#64748B', fontSize: '12px', '@media print': { color: '#475569 !important' } }}>{idx + 1}</TableCell>
                  <TableCell sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '12px', '@media print': { color: '#000000 !important' } }}>
                    {item.description}
                  </TableCell>
                  <TableCell sx={{ color: '#94A3B8', fontSize: '11px', fontFamily: 'monospace', '@media print': { color: '#475569 !important' } }}>
                    {item.sacCode || '998314'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#CBD5E1', fontSize: '12px', '@media print': { color: '#000000 !important' } }}>
                    {item.quantity}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#CBD5E1', fontSize: '12px', '@media print': { color: '#000000 !important' } }}>
                    {formatMoney(item.rate)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '12px', '@media print': { color: '#000000 !important' } }}>
                    {formatMoney(item.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Totals & Tax Calculation Breakdown */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3.5 }}>
            <Box sx={{ width: { xs: '100%', sm: '340px' }, p: 2, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.08)', '@media print': { bgcolor: '#F8FAFC !important', border: '1px solid #CBD5E1 !important' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '12px', color: '#94A3B8', '@media print': { color: '#475569 !important' } }}>
                <span>Taxable Value (Subtotal):</span>
                <span style={{ fontWeight: 600, color: '#FFFFFF' }}>{formatMoney(invoice.subtotal)}</span>
              </Box>

              {Number(invoice.cgstAmount) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '12px', color: '#94A3B8', '@media print': { color: '#475569 !important' } }}>
                  <span>CGST (9%):</span>
                  <span>{formatMoney(invoice.cgstAmount)}</span>
                </Box>
              )}

              {Number(invoice.sgstAmount) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '12px', color: '#94A3B8', '@media print': { color: '#475569 !important' } }}>
                  <span>SGST (9%):</span>
                  <span>{formatMoney(invoice.sgstAmount)}</span>
                </Box>
              )}

              {Number(invoice.igstAmount) > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '12px', color: '#94A3B8', '@media print': { color: '#475569 !important' } }}>
                  <span>IGST (18%):</span>
                  <span>{formatMoney(invoice.igstAmount)}</span>
                </Box>
              )}

              <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)', '@media print': { borderColor: '#E2E8F0 !important' } }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '14px', fontWeight: 800, color: '#22d3ee', '@media print': { color: '#0891b2 !important' } }}>
                <span>Total Invoice Value:</span>
                <span>{formatMoney(invoice.totalAmount)}</span>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '12px', color: '#10b981', '@media print': { color: '#059669 !important' } }}>
                <span>Total Amount Settled:</span>
                <span style={{ fontWeight: 700 }}>{formatMoney(invoice.paidAmount || 0)}</span>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.8, fontSize: '13px', fontWeight: 800, color: isPaid ? '#10b981' : '#f87171', borderTop: '1px dashed rgba(255, 255, 255, 0.15)', mt: 0.5, '@media print': { borderTop: '1px dashed #CBD5E1 !important' } }}>
                <span>Balance Due:</span>
                <span>{formatMoney(invoice.balanceDue || 0)}</span>
              </Box>
            </Box>
          </Box>

          {/* Official Bank Remittance Coordinates Box */}
          <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: 'rgba(6, 182, 212, 0.05)', border: '1px solid rgba(6, 182, 212, 0.25)', mb: 3.5, '@media print': { bgcolor: '#F0FDFA !important', border: '1px solid #99F6E4 !important' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BankIcon sx={{ color: '#06b6d4', fontSize: 18, '@media print': { color: '#0891b2 !important' } }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#22d3ee', letterSpacing: '0.03em', textTransform: 'uppercase', '@media print': { color: '#0891b2 !important' } }}>
                Official Remittance Coordinates (Wire Transfer & UPI)
              </Typography>
            </Box>
            <Grid container spacing={2} sx={{ fontSize: '12px' }}>
              <Grid item xs={12} sm={6}>
                <div><strong>Beneficiary:</strong> {accName}</div>
                <div><strong>Bank Name:</strong> {bankName}</div>
                <div><strong>Current A/C No:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{accNum}</span></div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <div><strong>IFSC Code:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{ifsc}</span> (Branch: {branch})</div>
                <div><strong>UPI Virtual ID:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{upiId}</span></div>
                <div style={{ color: '#94A3B8', marginTop: '2px' }}>Mode: NEFT / RTGS / IMPS / UPI</div>
              </Grid>
            </Grid>
          </Box>

          {/* Payment History Receipts (if any settlements recorded) */}
          {invoice.payments && invoice.payments.length > 0 && (
            <Box sx={{ mb: 3.5 }}>
              <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', mb: 1, '@media print': { color: '#059669 !important' } }}>
                Payment & Settlement Receipts Log ({invoice.payments.length})
              </Typography>
              <Table size="small" sx={{ border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 2, '@media print': { border: '1px solid #CBD5E1 !important' } }}>
                <TableHead sx={{ bgcolor: 'rgba(255, 255, 255, 0.03)', '@media print': { bgcolor: '#F1F5F9 !important' } }}>
                  <TableRow>
                    <TableCell sx={{ color: '#94A3B8', fontSize: '11px', fontWeight: 700 }}>DATE</TableCell>
                    <TableCell sx={{ color: '#94A3B8', fontSize: '11px', fontWeight: 700 }}>METHOD</TableCell>
                    <TableCell sx={{ color: '#94A3B8', fontSize: '11px', fontWeight: 700 }}>BANK UTR / REF #</TableCell>
                    <TableCell align="right" sx={{ color: '#94A3B8', fontSize: '11px', fontWeight: 700 }}>AMOUNT</TableCell>
                    <TableCell align="right" sx={{ color: '#94A3B8', fontSize: '11px', fontWeight: 700 }}>STATUS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.payments.map((p, pIdx) => (
                    <TableRow key={p.id || pIdx}>
                      <TableCell sx={{ color: '#CBD5E1', fontSize: '11px' }}>{p.paymentDate}</TableCell>
                      <TableCell sx={{ color: '#CBD5E1', fontSize: '11px', textTransform: 'uppercase' }}>{p.paymentMethod}</TableCell>
                      <TableCell sx={{ color: '#22d3ee', fontSize: '11px', fontFamily: 'monospace' }}>{p.transactionReference}</TableCell>
                      <TableCell align="right" sx={{ color: '#10b981', fontSize: '11px', fontWeight: 700 }}>{formatMoney(p.amount)}</TableCell>
                      <TableCell align="right">
                        <Chip label="SETTLED" size="small" sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#34d399', fontSize: '9px', height: '18px', fontWeight: 700 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Pending Client UTR Banner (if submitted and awaiting admin signoff) */}
          {invoice.clientUtrSubmission && !invoice.clientUtrSubmission.verified && (
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.3)', mb: 3.5, '@media print': { bgcolor: '#EFF6FF !important', border: '1px solid #BFDBFE !important' } }}>
              <Typography variant="body2" sx={{ color: '#60a5fa', fontWeight: 700, '@media print': { color: '#2563EB !important' } }}>
                ✓ Client Remittance UTR Submitted: {invoice.clientUtrSubmission.utrNumber}
              </Typography>
              <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mt: 0.3, '@media print': { color: '#475569 !important' } }}>
                Submitted on {invoice.clientUtrSubmission.submittedAt ? new Date(invoice.clientUtrSubmission.submittedAt).toLocaleDateString() : 'recently'}. Accounts department is reconciling with IndusInd Bank statement.
              </Typography>
            </Box>
          )}

          {/* Authorized Signatory Footnote */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.08)', '@media print': { borderTop: '1px solid #E2E8F0 !important' } }}>
            <Box sx={{ maxWidth: '400px' }}>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontStyle: 'italic', '@media print': { color: '#64748B !important' } }}>
                This is a computer-generated official tax invoice issued by Verve AI Labs Private Limited. All intellectual property, licenses, and deliverables remain subject to the master service agreement and non-disclosure terms.
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: '#94A3B8', display: 'block', mb: 3, '@media print': { color: '#334155 !important' } }}>
                For <strong>VERVEAILABS PRIVATE LIMITED</strong>
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#22d3ee', letterSpacing: '0.02em', '@media print': { color: '#0891b2 !important' } }}>
                Authorized Signatory
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                Finance & Corporate Treasury
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
